import { Box, Chip, Paper, Stack, TablePagination } from "@mui/material";
import { flexRender } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import {
  AppTable,
  AppTableBody,
  AppTableCell,
  AppTableContainer,
  AppTableHead,
  AppTableRow,
  AppTableSort,
} from "../../../components/common/app-table/ui";
import useDefaultTableUser from "../../../components/common/app-table/useDefaultTableUser";
import FormAddButton from "../../../components/common/FormAddButton";
import AppSearchField from "../../../components/common/forms/AppSearchField";
import { ExportButton } from "../../../components/common/primebmd-ui/DataButtons";
import { IAccounts, AccountsExport } from "./accounts-utils";
import accountsColumns from "./accountsColumn";
import {
  useGetUserQuery,
  useFormatGetAccountQuery,
} from "../../../hooks/useUser";
import LoadingIndicator from "../../../components/common/LoadingIndicator";
import { useSnackBar } from "../../../components/ToastProvider";
import { exportAccountToExcel } from "./accounts-export";
import { usePermission } from "../../../hooks/usePermissions";
import { Modules, Permissions } from "../Roles/roles-utils";


//quins
import { useState, useCallback, useMemo } from 'react';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useGetLocationsQuery } from '../../../hooks/useLocations';
import Typography from '@mui/material/Typography';
import { appColors } from '../../../theme';
import { getAccountsColumns } from './accountsColumn';
import { ILocation } from '../../DatabaseManagement/OtherDBs/Location/utils/Location-utils';

const AccountsTable = () => {
  const { showSnackBar } = useSnackBar();
  const hasAddPermission = usePermission(
    Modules.user.accessKey,
    Permissions.add
  );
  const hasEditPermission = usePermission(
    Modules.user.accessKey,
    Permissions.edit
  );
  const hasDeletePermission = usePermission(
    Modules.user.accessKey,
    Permissions.delete
  );
  const hasExportPermission = usePermission(
    Modules.user.accessKey,
    Permissions.export
  );

  const userData = useGetUserQuery();
  const data: IAccounts[] = userData?.data === undefined ? [] : userData.data;

  const formattedAccountData: AccountsExport[] = useFormatGetAccountQuery(data);

  const navigate = useNavigate();

  //quins 
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedUserForProjects, setSelectedUserForProjects] = useState<{
    user: IAccounts;
    projects: ILocation[];
  } | null>(null);
  const locationsQuery = useGetLocationsQuery();
  const allLocations: ILocation[] = (locationsQuery.data as unknown as ILocation[]) || [];

  const userProjectsMap = useMemo(() => {
    const map = new Map<string, ILocation[]>();
    for (const loc of allLocations) {
      if (loc.userIds && Array.isArray(loc.userIds)) {
        for (const uid of loc.userIds) {
          const list = map.get(uid) || [];
          list.push(loc);
          map.set(uid, list);
        }
      }
    }
    return map;
  }, [allLocations]);

  const handleOpenProjects = useCallback((user: IAccounts, projects: ILocation[], position: { top: number; left: number }) => {
    setSelectedUserForProjects({ user, projects });
    setAnchorPosition(position);
  }, []);

  const handleCloseProjects = () => {
    setAnchorPosition(null);
    setSelectedUserForProjects(null);
  };

  const columns = useMemo(
    () =>
      getAccountsColumns(userProjectsMap, handleOpenProjects).filter(
        (col) =>
          col.id !== 'actions' ||
          ((hasEditPermission || hasDeletePermission) && col.id === 'actions')
      ),
    [userProjectsMap, handleOpenProjects, hasEditPermission, hasDeletePermission]
  );

  const {
    table,
    dataCount,
    searchFilter,
    setSearchFilter,
    pageIndex,
    pageSize,
  } = useDefaultTableUser(
    data,
    columns
  );
  const emptyText = searchFilter ? "No data found" : "No data yet";

  const goToAddUserAccount = () => navigate("/users/new");

  const handleExport = () => {
    exportAccountToExcel(formattedAccountData, "User Report");
  };

  

  

  if (userData?.isLoading) return <LoadingIndicator show />;

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2} gap={2}>
        {hasExportPermission && <ExportButton onClick={handleExport} />}
        <AppSearchField
          onSearchKeywordChange={setSearchFilter}
          searchKeyword={searchFilter ?? ""}
        />
        {hasAddPermission && <FormAddButton onClick={goToAddUserAccount} />}
      </Stack>
      <Paper elevation={3}>
        <AppTableContainer>
          <AppTable stickyHeader>
            <AppTableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <AppTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <AppTableCell key={header.id} align="center">
                      <AppTableSort
                        canSort={header.column.getCanSort()}
                        currentSort={header.column.getIsSorted()}
                        onSort={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </AppTableSort>
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableHead>

            <AppTableBody
              dataCount={dataCount}
              loading={userData?.isLoading}
              emptyText={emptyText}
            >
              {table.getRowModel().rows.map((row) => (
                <AppTableRow key={row.id} sx={{ height: "45px" }}>
                  {row.getVisibleCells().map((cell) => (
                    <AppTableCell key={cell.id} align="center">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableBody>
          </AppTable>
        </AppTableContainer>
        <Box position="relative">
          <TablePagination
            sx={{ bgcolor: "white", borderTop: "1px solid #eee" }}
            component="div"
            count={dataCount}
            page={pageIndex}
            onPageChange={(e, page) => table.setPageIndex(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) =>
              table.setPageSize(Number(e.target.value))
            }
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Box>
      </Paper>
      {userData?.isError && showSnackBar("Network Error", "error")}
            <Popover
        open={Boolean(anchorPosition)}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || undefined}
        onClose={handleCloseProjects}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        BackdropProps={{
          invisible: true,
          sx: { backgroundColor: 'transparent' },
        }}
        PaperProps={{
          sx: {
            width: 340,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 380,
            p: 2,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            border: '1px solid #e0e0e0',
            bgcolor: '#fff',
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" pb={1} borderBottom="1px solid #eee">
          <Typography fontSize={15} fontWeight={600}>
            {selectedUserForProjects?.user.firstName || ''} {selectedUserForProjects?.user.lastName || ''} - Projects
          </Typography>
          <IconButton size="small" onClick={handleCloseProjects} sx={{ p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack gap={1} sx={{ maxHeight: 280, overflowY: 'auto', mt: 1.5, pr: 0.5 }}>
          {(!selectedUserForProjects?.projects || selectedUserForProjects.projects.length === 0) ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              No projects assigned to this user
            </Typography>
          ) : (
            selectedUserForProjects.projects.map((loc) => (
              <Box key={loc.id} sx={{ p: 1.2, borderRadius: 1, bgcolor: '#f9f9f9', border: '1px solid #eee' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontSize={14} fontWeight={500} color="text.primary">
                      {loc.name}{' '}
                      <Box component="span" sx={{ color: 'text.secondary', fontSize: 12 }}>
                        ({loc.type || 'N/A'})
                      </Box>
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={loc.isActive ? 'Active' : 'Inactive'}
                    color={loc.isActive ? 'primary' : 'default'}
                    variant={loc.isActive ? 'outlined' : 'filled'}
                    sx={{
                      flexShrink: 0,
                      height: 20,
                      fontSize: 11,
                      fontWeight: 500,
                      ...(!loc.isActive && {
                        backgroundColor: '#e0e0e0',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                      }),
                    }}
                  />
                </Stack>
              </Box>
            ))
          )}
        </Stack>
      </Popover>
    </>
  );
};

export default AccountsTable;
